import { LoadingSpinner } from "@kubdee/ui/custom/feedback/loading-spinner";
/**
 * React Router v7 route tree for Kubdee Platform.
 *
 * Route groups mirror the Next.js App Router convention:
 *   (auth)   — login / signup / forgot-password / reset-password / OAuth callback
 *   (public) — landing, pricing, invite
 *   (main)   — protected routes (desk + all modules)
 *
 * Every page component is lazy-loaded to keep the initial bundle small.
 * Layouts are eagerly imported because they are shared across many routes.
 */

import { lazy, Suspense } from "react";
import { createBrowserRouter, Outlet } from "react-router";

import { generatedModuleRoutes } from "@/generated/module-routes";
// /workflow standalone routes removed in #414 phase 4 — workflow editing
// lives in /settings/doctypes/{id}?tab=workflow (Doctype Admin).
import { adminRoutes } from "@/routes/admin";
import { settingsRoutes } from "@/routes/settings";
import { meRoutes } from "@/routes/me";

// ---------------------------------------------------------------------------
// Layouts (eagerly loaded — shared across many routes)
// ---------------------------------------------------------------------------

// MainLayout provides auth guard + WebSocket + CommandPalette
import MainLayout from "@/app/(main)/layout";

// Module layouts (each provides SidebarProvider + AppSidebar + ModuleGuard)
import AdminLayout from "@/app/(main)/admin/layout";
import SettingsLayout from "@/app/(main)/settings/layout";
import MeLayout from "@/app/(main)/me/layout";

// ---------------------------------------------------------------------------
// Lazy page imports — auth / public / standalone
// ---------------------------------------------------------------------------

// Auth pages
const Login = lazy(() => import("@/app/(auth)/login/page"));
const Signup = lazy(() => import("@/app/(auth)/signup/page"));
const ForgotPassword = lazy(() => import("@/app/(auth)/forgot-password/page"));
const ResetPassword = lazy(() => import("@/app/(auth)/reset-password/page"));
const GoogleCallback = lazy(
  () => import("@/app/(auth)/auth/google/callback/page")
);

// Public pages
const Landing = lazy(() => import("@/app/page"));
const Privacy = lazy(() => import("@/app/(public)/privacy/page"));
const Terms = lazy(() => import("@/app/(public)/terms/page"));
const InviteToken = lazy(() => import("@/app/(public)/invite/[token]/page"));

// Standalone (no group)
const Onboarding = lazy(() => import("@/app/onboarding/page"));
const SelectTenant = lazy(() => import("@/app/select-tenant/page"));
const NotFound = lazy(() => import("@/app/not-found"));

// Desk (main index)
const Desk = lazy(() => import("@/app/(main)/desk/page"));

// Doctype Admin Epic (#347) A7 (#355) — dev-only standalone demo for the
// `<DoctypeForm>` component. Not wired into any sidebar — developers reach
// it by typing `/_dev/doctype-form-demo` into the URL bar. Safe to ship
// because the route sits under the authenticated `ProtectedLayout` + is
// only discoverable via direct navigation.
const DoctypeFormDemo = lazy(
  () => import("@/app/(main)/_dev/doctype-form-demo/page")
);

// ---------------------------------------------------------------------------
// Loading fallback for Suspense boundaries
// ---------------------------------------------------------------------------

function PageLoading() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <LoadingSpinner size="3xl" className="text-muted-foreground" />
        <p className="text-sm text-muted-foreground">กำลังโหลด...</p>
      </div>
    </div>
  );
}

/**
 * Wraps child routes in a Suspense boundary so lazy components show a spinner.
 */
function SuspenseLayout() {
  return (
    <Suspense fallback={<PageLoading />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * Main protected layout: auth guard + Suspense for lazy children.
 */
function ProtectedLayout() {
  return (
    <MainLayout>
      <Suspense fallback={<PageLoading />}>
        <Outlet />
      </Suspense>
    </MainLayout>
  );
}

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const router = createBrowserRouter([
  {
    // Wrapper with Suspense for top-level lazy routes
    Component: SuspenseLayout,
    children: [
      // ── Auth routes ──────────────────────────────────────────────────
      { path: "/login", Component: Login },
      { path: "/signup", Component: Signup },
      { path: "/forgot-password", Component: ForgotPassword },
      { path: "/reset-password", Component: ResetPassword },
      { path: "/auth/google/callback", Component: GoogleCallback },

      // ── Public routes ────────────────────────────────────────────────
      { path: "/", Component: Landing },
      { path: "/privacy", Component: Privacy },
      { path: "/terms", Component: Terms },
      { path: "/invite/:token", Component: InviteToken },

      // ── Standalone routes ────────────────────────────────────────────
      { path: "/onboarding", Component: Onboarding },
      { path: "/select-tenant", Component: SelectTenant },

      // ── Protected (main) routes ──────────────────────────────────────
      {
        Component: ProtectedLayout,
        children: [
          { path: "/desk", Component: Desk },

          // Doctype Admin Epic (#347) A7 (#355) — dev-only DoctypeForm demo.
          { path: "/_dev/doctype-form-demo", Component: DoctypeFormDemo },

          // Each module wraps its routes with its own generated Layout
          // (provides SidebarProvider + AppSidebar + ModuleGuard)
          ...generatedModuleRoutes,
          { path: "/admin", Component: AdminLayout, children: adminRoutes },
          {
            path: "/settings",
            Component: SettingsLayout,
            children: settingsRoutes,
          },
          { path: "/me", Component: MeLayout, children: meRoutes },
        ],
      },

      // ── Catch-all 404 ────────────────────────────────────────────────
      { path: "*", Component: NotFound },
    ],
  },
]);
